import React, { ReactNode, useMemo } from "react";
import { useImmer } from "use-immer";

import { IgChip, IgTooltip } from "@intelligems/ig-theme";
import {
  AudienceDto,
  ChannelType,
  DeviceType,
  ExperiencePageTargetingFe,
  VisitorType,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useGetShopDomain } from "@src/api/shops/get-domain";
import { ExpressionOverview } from "@src/components/targeting/components/audienceTargeting/CommonExpression/ExpressionOverview";
import {
  formatQueries,
  getExpressionsState,
  getInitialChannelsState,
  getInitialCountriesState,
  getInitialDeviceState,
  getInitialVisitorState,
} from "@src/components/targeting/components/audienceTargeting/utils";
import { renderConditionText } from "@src/features/experiments/utils/validators";
import {
  AdvancedTargetingChip,
  AllVisitorsChip,
  CurrencyTargetingChip,
  LinkTargetingChip,
  PageTargetingChip,
} from "@src/features/sharedExperience/components/targeting/targetingOverviewRenderers";
import { TempTargetingTooltip } from "@src/features/sharedExperience/components/targeting/TempTargetingTooltip";
import { Experience } from "@src/store/slices/experiences/types";
import { copyToClipboard } from "@src/utils/copyToClipboard";

interface TargetingOverviewProps {
  audienceState: AudienceDto | undefined | null;
  experience: Experience;
  pageTargeting: ExperiencePageTargetingFe[] | undefined | null;
  onPageTargetingChipClick?: () => void;
  chipColor?: "info" | "error" | "warning" | "success" | "accent" | "default";
  hideTooltip?: boolean;
  groupLabelCategories?: boolean;
  onGeneralChipClick?: () => void;
  cutOffAt?: number;
  separateExpressions?: boolean;
}

export const TargetingOverview = ({
  audienceState,
  experience,
  pageTargeting,
  onPageTargetingChipClick,
  chipColor,
  hideTooltip,
  groupLabelCategories,
  onGeneralChipClick,
  cutOffAt,
  separateExpressions,
}: TargetingOverviewProps) => {
  const domain = useGetShopDomain();
  const [copyClicked, setCopyClicked] = useImmer(false);

  const onLinkCopy = async () => {
    await copyToClipboard(
      `${domain.data ? `${domain.data}/` : ""}${
        experience.linkBaseUrl || ""
      }?ig-campaign=${experience.id?.slice(-12)}`
    );

    setCopyClicked(true);
    setTimeout(() => setCopyClicked(false), 2000);
  };

  const getExpressionsList = (index: number) => {
    if (audienceState) {
      const device = getInitialDeviceState(audienceState, index);
      const visitor = getInitialVisitorState(audienceState, index);
      const channel = getInitialChannelsState(audienceState, index);
      const country = getInitialCountriesState(audienceState, index);

      return getExpressionsState(
        device as DeviceType,
        visitor as VisitorType,
        country,
        channel as ChannelType[]
      );
    }
  };

  const targetingChipArray = useMemo(() => {
    const componentArray: ReactNode[] = [];

    if (pageTargeting && pageTargeting.length > 0) {
      componentArray.push(
        <PageTargetingChip
          audienceState={audienceState}
          chipColor={chipColor}
          onPageTargetingChipClick={onPageTargetingChipClick}
          experience={experience}
          hideTooltip={hideTooltip}
          onGeneralChipClick={onGeneralChipClick}
        />
      );
    }
    if (experience.requiresLink) {
      componentArray.push(
        <LinkTargetingChip
          audienceState={audienceState}
          experience={experience}
          chipColor={chipColor}
          onLinkChipClick={onLinkCopy}
          copyClicked={copyClicked}
          hideTooltip={hideTooltip}
          onGeneralChipClick={onGeneralChipClick}
        />
      );
    } else if (
      !audienceState ||
      (audienceState.filters.length === 0 &&
        !audienceState.excludeCurrency.exclude)
    ) {
      componentArray.push(
        <AllVisitorsChip
          audienceState={audienceState}
          experience={experience}
          chipColor={chipColor}
          hideTooltip={hideTooltip}
          onGeneralChipClick={onGeneralChipClick}
        />
      );
    }

    if (
      audienceState &&
      audienceState.audienceType === "advanced" &&
      audienceState.filters.length > 0
    ) {
      componentArray.push(
        <AdvancedTargetingChip
          audienceState={audienceState}
          experience={experience}
          chipColor={chipColor}
          onGeneralChipClick={onGeneralChipClick}
        />
      );
    } else if (audienceState && audienceState.filters.length > 0) {
      if (separateExpressions) {
        audienceState.filters.forEach((filter, index) => {
          if (filter.expressionType === "custom") {
            componentArray.push(
              <IgTooltip
                title={
                  hideTooltip ? (
                    ""
                  ) : (
                    <>
                      {"Shown only to: " +
                        renderConditionText(filter.expression || [])}
                      {experience.category === "personalization" && (
                        <TempTargetingTooltip audienceState={audienceState} />
                      )}
                    </>
                  )
                }
              >
                <IgChip
                  color={chipColor || "success"}
                  label={"Custom Audience"}
                  clickable={onGeneralChipClick !== undefined}
                  onClick={onGeneralChipClick}
                />
              </IgTooltip>
            );
          } else {
            const expressionsList = getExpressionsList(index);
            if (!expressionsList || (expressionsList || []).length === 0)
              return null;

            expressionsList.forEach((expression, index) => {
              componentArray.push(
                <IgTooltip
                  title={
                    hideTooltip ? (
                      ""
                    ) : (
                      <>
                        {"Shown only to: " +
                          renderConditionText(filter.expression || [])}
                        {experience.category === "personalization" && (
                          <TempTargetingTooltip audienceState={audienceState} />
                        )}
                      </>
                    )
                  }
                >
                  <IgChip
                    clickable={onGeneralChipClick !== undefined}
                    onClick={onGeneralChipClick}
                    color={chipColor || "default"}
                    key={expression.type}
                    label={`${formatQueries(
                      expression.queries.slice(
                        0,
                        cutOffAt !== undefined &&
                          expression.queries.length > cutOffAt
                          ? cutOffAt
                          : expression.queries.length
                      ),
                      groupLabelCategories
                    )} ${!groupLabelCategories && cutOffAt !== undefined && expression.queries.length > cutOffAt ? `+ ${expression.queries.length - cutOffAt} more` : ""}`}
                  />
                </IgTooltip>
              );
            });
          }
        });
      } else {
        componentArray.push(
          <Grid container direction={"row"}>
            {audienceState.filters.map((filter, index) => (
              <Grid item key={index}>
                <IgTooltip
                  title={
                    hideTooltip ? (
                      ""
                    ) : (
                      <>
                        {"Shown only to: " +
                          renderConditionText(filter.expression || [])}
                        {experience.category === "personalization" && (
                          <TempTargetingTooltip audienceState={audienceState} />
                        )}
                      </>
                    )
                  }
                >
                  {filter.expressionType === "custom" ? (
                    <IgChip
                      color={chipColor || "success"}
                      label={"Custom Audience"}
                      clickable={onGeneralChipClick !== undefined}
                      onClick={onGeneralChipClick}
                    />
                  ) : (
                    <ExpressionOverview
                      chipColor={chipColor || "info"}
                      expressionsList={getExpressionsList(index)!}
                      cutOffAt={3}
                      groupLabelCategories={groupLabelCategories}
                      onGeneralChipClick={onGeneralChipClick}
                    />
                  )}
                </IgTooltip>
              </Grid>
            ))}
          </Grid>
        );
      }
    }

    if (audienceState?.excludeCurrency?.exclude) {
      componentArray.push(
        <CurrencyTargetingChip
          audienceState={audienceState}
          experience={experience}
          chipColor={chipColor}
          hideTooltip={hideTooltip}
          onGeneralChipClick={onGeneralChipClick}
        />
      );
    }
    return componentArray;
  }, [audienceState, experience, pageTargeting, copyClicked]);

  return (
    <Grid item container direction={"row"} columnSpacing={1}>
      {targetingChipArray
        .slice(
          0,
          cutOffAt !== undefined && targetingChipArray.length > cutOffAt
            ? cutOffAt
            : targetingChipArray.length
        )
        .map((chip, index) => (
          <Grid item key={index}>
            {chip}
          </Grid>
        ))}
      {cutOffAt !== undefined && targetingChipArray.length > cutOffAt ? (
        <Grid item>
          <IgChip label={`+${targetingChipArray.length - cutOffAt}`} />
        </Grid>
      ) : null}
    </Grid>
  );
};
